import { apiSlice } from "./apiSlice";

const USERS_URL = "/users";

import type { Group, GroupStatus } from "components/Circle/ViewGroupImproved";

export interface PaymentStatusResponse {
  isPayee: boolean;
  status: string;
  canPayout: boolean;
  allPaid: boolean;
  verificationDeadline: string | null;
  bankDeadline: string | null;
  myPayoutDate: string | null;
}

// ============================================
// PAYMENT HISTORY TYPES
// ============================================

export type TransactionType = "DEPOSIT" | "WITHDRAWAL";
export type TransactionStatus = "COMPLETED" | "PENDING" | "FAILED";

export interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  currency: string;
  status: TransactionStatus;
  date: string;
  circleId: string;
  circleName: string;
  description: string;
}

export interface PaymentHistorySummary {
  totalDeposits: number;
  totalWithdrawals: number;
  netBalance: number;
}

export interface PaymentHistoryResponse {
  transactions: Transaction[];
  nextCursor: string | null;
  summary: PaymentHistorySummary;
}

export interface PaymentHistoryParams {
  type?: "DEPOSIT" | "WITHDRAWAL" | "all";
  cursor?: string;
  limit?: number;
}

export interface NewUser {
  cognitoId?: string;
  email: string;
  firstName: string;
  lastName: string;
  country?: string;
}

export interface OnboardingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
}

export interface OnboardingData {
  address: OnboardingAddress;
  country: string;
  phone: string;
  dateOfBirth?: string;
}

// ============================================
// VERIFICATION STATUS TYPES (Stripe Connect)
// ============================================

export interface VerificationRequirementError {
  code: string;
  reason: string;
  requirement: string;
}

export interface VerificationAlternative {
  alternative_fields_due: string[];
  original_fields_due: string[];
}

export interface VerificationRequirements {
  alternatives: VerificationAlternative[];
  current_deadline: string | null;
  currently_due: string[];
  disabled_reason: string | null;
  errors: VerificationRequirementError[];
  eventually_due: string[];
  past_due: string[];
  pending_verification: string[];
}

export interface VerificationStatusResponse {
  account: {
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requirements: VerificationRequirements;
  };
  userStatus: string;
  verification: {
    lastError: string;
    sessionId: string;
    status: string;
  };
  isVerified: boolean;
}

// Onboarding Status Response (from /users/onboarding)
export interface OnboardingStatusResponse {
  status: "pending" | "verifying" | "active" | "verification_failed" | "banned";
  country: string;
  stripeData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    dob: {
      day: number;
      month: number;
      year: number;
      formatted: string;
    } | null;
    address: {
      line1: string;
      line2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    } | null;
  };
  requirements: {
    currently_due: string[];
    eventually_due: string[];
    pending_verification: string[];
    disabled_reason: string | null;
    charges_enabled: boolean;
    payouts_enabled: boolean;
  };
  hasBasicInfo: boolean;
  hasDob: boolean;
  hasAddress: boolean;
  hasPhone: boolean;
  bankSkipped: boolean;
}

// ============================================
// BANK ACCOUNT TYPES
// ============================================

// Canada Bank Account Request
export interface AddBankAccountCA {
  country: "CA";
  accountHolderName: string;
  institutionNumber: string; // 3 digits (e.g., "001")
  transitNumber: string; // 5 digits (e.g., "12345")
  accountNumber: string; // 7-12 digits
}

// USA Bank Account Request
export interface AddBankAccountUS {
  country: "US";
  accountHolderName: string;
  routingNumber: string; // 9 digits
  accountNumber: string; // 4-17 digits
}

export type AddBankAccountRequest = AddBankAccountCA | AddBankAccountUS;

// Bank Account Response
export interface BankAccount {
  id: string;
  bank_name: string;
  last4: string;
  country: string;
  currency: string;
  status: string;
  default_for_currency: boolean;
}

export interface AddBankAccountResponse {
  success: boolean;
  bankAccount: BankAccount;
}

export interface GetBankAccountsResponse {
  bankAccounts: BankAccount[];
}

export interface DeleteBankAccountResponse {
  success: boolean;
  message: string;
}

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createUser: builder.mutation({
      query: (data: NewUser) => ({
        url: `${USERS_URL}/signup`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),
    logout: builder.mutation({
      query: () => ({
        url: `${USERS_URL}/logout`,
        method: "POST",
      }),
    }),
    myCircles: builder.mutation({
      query: (userId) => ({
        url: `${USERS_URL}/my_circles/${userId}`,
        method: "GET",
      }),
    }),
    myActiveCircles: builder.query<Group[] | [], { status?: GroupStatus }>({
      query: ({ status }) => ({
        url: `${USERS_URL}/active-circles`,
        params: status ? { status } : {},
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Circle" as const, id })),
              { type: "Circle", id: "LIST" },
            ]
          : [{ type: "Circle", id: "LIST" }],
    }),
    userStatus: builder.mutation({
      query: ({ groupId }) => ({
        url: `${USERS_URL}/group_status`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          groupId,
        },
      }),
    }),
    getActiveStatus: builder.query<PaymentStatusResponse, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `${USERS_URL}/active/${groupId}`,
        method: "GET",
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "Status", id: groupId },
      ],
    }),
    cashAction: builder.mutation({
      query: ({ groupId, action }) => ({
        url: `${USERS_URL}/payments`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          groupId,
          action,
        },
      }),
    }),
    deposit: builder.mutation({
      query: ({ groupId, action }) => ({
        url: `${USERS_URL}/payments/deposit`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          groupId,
          action,
        },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "Circle", id: groupId },
      ],
    }),
    withdraw: builder.mutation({
      query: ({ groupId, action }) => ({
        url: `${USERS_URL}/payments/withdraw`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          groupId,
          action,
        },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "Circle", id: groupId },
      ],
    }),
    updatePaymentMethod: builder.mutation({
      query: ({ paymentIntentId, paymentMethodType }) => ({
        url: `${USERS_URL}/payments/update-method`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          paymentIntentId,
          paymentMethodType,
        },
      }),
    }),
    confirmDeposit: builder.mutation({
      query: ({ paymentIntentId, circleId }) => ({
        url: `${USERS_URL}/payments/confirm`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          paymentIntentId,
          circleId,
        },
      }),
      invalidatesTags: (result, error, { circleId }) => [
        { type: "Status", id: circleId },
        { type: "Circle", id: circleId },
      ],
    }),
    getAppeal: builder.mutation({
      query: (userId) => ({
        url: `${USERS_URL}/appeal/${userId}`,
        method: "GET",
      }),
    }),
    sendAppeal: builder.mutation({
      query: ({ userId, appealBody }) => ({
        url: `${USERS_URL}/appeal`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: { userId, appealBody },
      }),
    }),
    unBanMe: builder.mutation({
      query: (userId) => ({
        url: `${USERS_URL}/unban/${userId}`,
        method: "POST",
      }),
    }),
    addUser: builder.mutation({
      query: ({ userId, circleId }) => ({
        url: `${USERS_URL}/newMember`,
        method: "POST",
        body: { userId, circleId },
      }),
      invalidatesTags: ({ circleId }) => [
        { type: "CircleMembers", id: circleId },
      ],
    }),
    getProfile: builder.query<any, void>({
      query: () => ({
        url: `${USERS_URL}/profile`,
        method: "GET",
      }),
      providesTags: ["Profile"],
    }),
    getStatus: builder.query<any, void>({
      query: () => ({
        url: `${USERS_URL}/status`,
        method: "GET",
      }),
    }),
    emailExists: builder.query<{ exists: boolean }, { email: string }>({
      query: ({ email }) => ({
        method: "POST",
        url: `${USERS_URL}/exists`,
        body: { email },
      }),
    }),
    health: builder.query<{ status: string }, void>({
      query: () => ({
        url: `${USERS_URL}/health`,
        method: "GET",
      }),
    }),
    updateProfilePic: builder.mutation({
      query: ({ profilePicture }) => ({
        url: `${USERS_URL}/profile-pic`,
        method: "POST",
        body: { profilePicture },
      }),
      invalidatesTags: ["Profile"],
    }),
    updatePhone: builder.mutation<{ phone: string | null }, { phone: string | null }>({
      query: ({ phone }) => ({
        url: `${USERS_URL}/phone`,
        method: "PATCH",
        body: { phone },
      }),
      invalidatesTags: ["Profile"],
    }),
    // Onboarding endpoints
    updateOnboarding: builder.mutation<void, OnboardingData>({
      query: (data) => ({
        url: `${USERS_URL}/onboarding`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Profile", "OnboardingStatus"],
    }),
    acceptTerms: builder.mutation<void, { tosAcceptance: { date: string } }>({
      query: (data) => ({
        url: `${USERS_URL}/tos-acceptance`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Profile", "OnboardingStatus", "TosAcceptance"],
    }),
    getTosAcceptance: builder.query<{ tosAcceptance: { date: string } }, void>({
      query: () => ({
        url: `${USERS_URL}/tos-acceptance`,
        method: "GET",
      }),
      providesTags: ["TosAcceptance"],
    }),
    createIdentitySession: builder.mutation<
      { client_secret: string; session_id: string; attemptsRemaining: number; retryAfter?: string },
      void
    >({
      query: () => ({
        url: `${USERS_URL}/identity/create-session`,
        method: "POST",
      }),
    }),
    getVerificationStatus: builder.query<VerificationStatusResponse, void>({
      query: () => ({
        url: `${USERS_URL}/verification-status`,
        method: "GET",
      }),
      providesTags: ["VerificationStatus"],
    }),

    // Get onboarding status with Stripe data
    getOnboardingStatus: builder.query<OnboardingStatusResponse, void>({
      query: () => ({
        url: `${USERS_URL}/onboarding`,
        method: "GET",
      }),
      providesTags: ["OnboardingStatus"],
    }),

    // ============================================
    // BANK ACCOUNT ENDPOINTS
    // ============================================

    // Get all bank accounts
    getBankAccounts: builder.query<GetBankAccountsResponse, void>({
      query: () => ({
        url: `${USERS_URL}/bank`,
        method: "GET",
      }),
      providesTags: ["BankAccounts"],
    }),

    // Add bank account (works for both CA and US)
    addBankAccount: builder.mutation<
      AddBankAccountResponse,
      AddBankAccountRequest
    >({
      query: (data) => ({
        url: `${USERS_URL}/bank/add`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["BankAccounts"],
    }),

    // Skip bank setup (persists choice to DB)
    skipBankSetup: builder.mutation<{ bankSkipped: boolean }, void>({
      query: () => ({
        url: `${USERS_URL}/bank/skip`,
        method: "POST",
      }),
      invalidatesTags: ["OnboardingStatus"],
    }),

    // Delete bank account
    deleteBankAccount: builder.mutation<DeleteBankAccountResponse, string>({
      query: (bankId) => ({
        url: `${USERS_URL}/bank/${bankId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BankAccounts"],
    }),

    // Set bank account as default
    setDefaultBankAccount: builder.mutation<{ success: boolean }, string>({
      query: (bankId) => ({
        url: `${USERS_URL}/bank/${bankId}/default`,
        method: "POST",
      }),
      invalidatesTags: ["BankAccounts"],
    }),
    // Payment history
    getPaymentHistory: builder.query<
      PaymentHistoryResponse,
      PaymentHistoryParams
    >({
      query: ({ type = "all", cursor, limit = 20 }) => {
        const params = new URLSearchParams();
        if (type !== "all") params.append("type", type);
        if (cursor) params.append("cursor", cursor);
        params.append("limit", String(limit));
        return {
          url: `${USERS_URL}/payment-history?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PaymentHistory"],
    }),
  }),
});

export const {
  useCreateUserMutation,
  useLogoutMutation,
  useMyCirclesMutation,
  useMyActiveCirclesQuery,
  useUserStatusMutation,
  useGetActiveStatusQuery,
  useCashActionMutation,
  useDepositMutation,
  useWithdrawMutation,
  useUpdatePaymentMethodMutation,
  useConfirmDepositMutation,
  useGetAppealMutation,
  useSendAppealMutation,
  useUnBanMeMutation,
  useAddUserMutation,
  useGetProfileQuery,
  useUpdatePhoneMutation,
  useGetStatusQuery,
  useEmailExistsQuery,
  useHealthQuery,
  useUpdateProfilePicMutation,
  // Onboarding hooks
  useUpdateOnboardingMutation,
  useAcceptTermsMutation,
  useGetTosAcceptanceQuery,
  useCreateIdentitySessionMutation,
  useGetVerificationStatusQuery,
  useLazyGetVerificationStatusQuery,
  useGetOnboardingStatusQuery,
  useLazyGetOnboardingStatusQuery,
  // Bank account hooks
  useGetBankAccountsQuery,
  useAddBankAccountMutation,
  useDeleteBankAccountMutation,
  useSetDefaultBankAccountMutation,
  useSkipBankSetupMutation,
  // Payment history
  useGetPaymentHistoryQuery,
} = usersApiSlice;
